import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DiscountApiData } from 'src/app/pages/new-billing/types/api.types';

@Component({
  selector: 'app-upsell-buy-confirm',
  templateUrl: './upsell-buy-confirm.component.html',
  styleUrls: ['./upsell-buy-confirm.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsellBuyConfirmComponent {
  // Controls visibility when used without *ngIf
  @Input() show = true;

  // Text parts
  @Input() chargeText?: string; // project can pass a richer text if needed
  @Input() chargeUnitText?: string | null; // or 'per account'; set to null to hide
  @Input() showActivationLine = false; // default hidden; parent decides based on page
  @Input() activationText?: string;

  // Labels
  @Input() confirmLabel?: string;
  @Input() cancelLabel?: string;

  // Loading state for confirm button
  @Input() loading = false;

  // Optional custom icon source
  @Input() iconSrc = '/assets/images/benchmarks/icon.svg';
  @Input() iconAlt?: string;

  // Tracking
  @Input() trackLocation: string | null = null;
  @Input() trackUrl: string | null = null;

  // Discount functionality
  @Input() enableDiscount = false;
  @Input() discountCodeValue = '';
  @Input() appliedDiscount: { discount: DiscountApiData, code: string } | null = null;
  @Input() validatingDiscount = false;
  @Input() discountValidationError: string | null = null;
  @Input() showingDiscountInput = false;
  @Input() originalPrice?: string;
  @Input() discountedPrice?: string;
  @Input() formattedPrice?: string;
  @Input() confirmationTextPrefix?: string;
  @Input() confirmationTextSuffix?: string;

  @Output() confirm = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() applyDiscount = new EventEmitter<void>();
  @Output() clearDiscount = new EventEmitter<void>();
  @Output() showDiscountInput = new EventEmitter<void>();
  @Output() discountCodeChange = new EventEmitter<string>();
  @Output() discountKeyPress = new EventEmitter<KeyboardEvent>();

  constructor(private translate: TranslateService) {}

  getAppliedDiscountText(): string {
    if (!this.appliedDiscount) {
      return '';
    }

    const { discount } = this.appliedDiscount;
    const params = { value: discount.value };

    if (discount.type === 'percentage') {
      return this.translate.instant('upsell.discount.percentage', params);
    } else if (discount.type === 'amount') {
      return this.translate.instant('upsell.discount.amount', params);
    } else if (discount.type === 'amount_per_seat') {
      return this.translate.instant('upsell.discount.amount_per_seat', params);
    }
    return this.translate.instant('upsell.discount.applied');
  }

  /**
   * Determines if the confirm button should be disabled
   * Disabled when:
   * - Loading state is active
   * - OR when discount input is visible and no valid discount is applied
   */
  get isConfirmDisabled(): boolean {
    if (this.loading) {
      return true;
    }

    // Disable if discount input is showing and no valid discount is applied
    if (this.enableDiscount && this.showingDiscountInput && !this.appliedDiscount) {
      return true;
    }

    return false;
  }
}
